import React, { useState, useEffect } from 'react';
import Card from './Card';
import Button from './Button';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import PlusCircleIcon from '../icons/PlusCircleIcon';
import CloseCircleIcon from '../icons/CloseCircleIcon';
import CloseIcon from '../icons/CloseIcon';

interface EditStoreNoticeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  initialData?: {
    id?: string;
    title?: string;
    content?: string;
    tags?: string[];
    startDate?: string;
    endDate?: string;
  };
  onSave: (noticeData: {
    title: string;
    content: string;
    tags: string[];
    startDate: string;
    endDate: string;
    targetType: 'class' | 'subject';
  }) => void;
}

const EditStoreNoticeDialog: React.FC<EditStoreNoticeDialogProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  onSave
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setTitle(initialData.title || '');
      setContent(initialData.content || '');
      setTags(initialData.tags || []);
      setStartDate(initialData.startDate || '');
      setEndDate(initialData.endDate || '');
    } else {
      // 새 공지사항 모드일 때 초기화
      setTitle('');
      setContent('');
      setTags([]);
      setNewTag('');
      setStartDate('');
      setEndDate('');
    }
  }, [mode, initialData, isOpen]);

  const handleAddTag = () => {
    if (newTag.trim() && tags.length < 3 && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSave = () => {
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    if (!content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }
    if (!startDate) {
      alert('시작 날짜를 선택해주세요.');
      return;
    }
    if (!endDate) {
      alert('종료 날짜를 선택해주세요.');
      return;
    }

    onSave({
      title: title.trim(),
      content: content.trim(),
      tags,
      startDate,
      endDate,
      targetType: 'class' // 기본값, 필요시 props로 받을 수 있음
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: 'Pretendard'
    }}>
      <Card
        width="450px"
        height="auto"
        padding="24px 28px"
        borderRadius="16px"
        background="var(--Color-background-normal, white)"
        boxShadow="0px 8px 16px rgba(0, 0, 0, 0.12)"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* 헤더 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '8px'
        }}>
          <div style={{
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h2 style={{
                margin: 0,
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '20px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                {mode === 'create' ? '새 공지사항 등록하기' : '공지사항 수정하기'}
              </h2>
            </div>
            <div style={{
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '16px',
              fontFamily: 'pretendard',
              fontWeight: '500',
              lineHeight: '24px'
            }}>
              {mode === 'create' ? '상점 내 공지사항을 등록할 수 있어요.' : '등록된 공지사항을 수정할 수 있어요.'}
            </div>
          </div>
          
          {/* 닫기 버튼 */}
          <div 
            style={{
              padding: '4px',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              cursor: 'pointer'
            }}
            onClick={onClose}
          >
            <CloseIcon width={24} height={24} color="#0D0D0D" />
          </div>
        </div>

        {/* 폼 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          {/* 제목 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <InputField
              value={title}
              onChange={setTitle}
              placeholder="공지사항 제목을 입력해 주세요"
              width="100%"
              height="48px"
            />
          </div>

          {/* 내용 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <TextAreaField
              value={content}
              onChange={setContent}
              placeholder="공지사항의 내용을 입력해 주세요."
              width="100%"
              height="84px"
              rows={3}
            />
          </div>

          {/* 태그 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '16px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '24px'
              }}>
                공지사항 태그
              </div>
              <div style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.50px'
              }}>
                *태그는 최대 3개까지 입력 가능합니다.
              </div>
            </div>

            {/* 기존 태그들 */}
            {tags.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                {tags.map((tag, index) => (
                  <div
                    key={index}
                    style={{
                      height: '32px',
                      padding: '8px 12px',
                      background: 'var(--Color-fill-normal, #293044)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{
                      color: 'var(--Color-text-inverse, white)',
                      fontSize: '14px',
                      fontFamily: 'pretendard',
                      fontWeight: '400',
                      lineHeight: '21px'
                    }}>
                      {tag}
                    </span>
                    <div 
                      style={{
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleRemoveTag(tag)}
                    >
                      <CloseCircleIcon width={12.8} height={12.8} color="white" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 새 태그 입력 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <InputField
                value={newTag}
                onChange={setNewTag}
                placeholder="태그 입력 (선택 사항)"
                width="354px"
                height="48px"
                disabled={tags.length >= 3}
              />
              <div 
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: tags.length >= 3 ? 'not-allowed' : 'pointer',
                  opacity: tags.length >= 3 ? 0.5 : 1
                }}
                onClick={handleAddTag}
              >
                <PlusCircleIcon width={24} height={24} color="#989898" />
              </div>
            </div>
          </div>

          {/* 공지 기간 설정 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{
              color: 'var(--Color-text-normal, #161616)',
              fontSize: '16px',
              fontFamily: 'pretendard',
              fontWeight: '600',
              lineHeight: '24px'
            }}>
              공지 기간 설정
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <InputField
                type="date"
                value={startDate}
                onChange={setStartDate}
                placeholder="시작 날짜 선택"
                width="50%"
                height="48px"
              />
              <InputField
                type="date"
                value={endDate}
                onChange={setEndDate}
                placeholder="종료 날짜 선택"
                width="50%"
                height="48px"
              />
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Button
            width="124px"
            height="48px"
            variant="cancel"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            width="262px"
            height="48px"
            variant="submit"
            onClick={handleSave}
          >
            {mode === 'create' ? '등록' : '수정'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EditStoreNoticeDialog;
